import { EventBus } from '@pp/toolkit';

import type { PlaybackStats } from '../../components/video_v8/types';
import { StreamQuality } from '../../store/slices/settings/types';

import {
  type UninitialisedVideoControllerVersion,
  VideoPlayerEvent,
  HostEvent,
  type VideoPlayerConfig,
  type VideoServiceConfig,
} from './types';
import type { VideoServiceBus } from './VideoServiceBus';
import { VideoServicePlayback } from './VideoServicePlayback';

export class VideoService extends EventBus<{
  videoElement: (videoElement: HTMLVideoElement) => void;
  qualityLimit: (qualityLimit: number) => void;
  videoLatency: (latency: number) => void;
  playbackStats: (stats: PlaybackStats) => void;
  resetStreamQualityLimit: (qualityLimit: number) => void;
}> {
  coldStart: boolean = true;
  private _ready = false;
  private _videoPlayerConfig: VideoPlayerConfig | null = null;

  private config: VideoServiceConfig | null = null;

  public videoElement: HTMLVideoElement | null = null;
  public version: UninitialisedVideoControllerVersion = 0;
  public name: string = 'unknown';

  get configured(): boolean {
    return Boolean(this.config);
  }

  /* readyToPlay */
  set ready(value: boolean) {
    this._ready = value;
    this.attemptStart();
  }
  get ready(): boolean {
    return this._ready;
  }

  /* videoPlayerConfig */
  set videoPlayerConfig(value: VideoPlayerConfig) {
    this._videoPlayerConfig = value;
    this.attemptSetup();
  }
  get videoPlayerConfig(): VideoPlayerConfig | null {
    return this._videoPlayerConfig;
  }

  constructor(
    private bus: VideoServiceBus,
    public playback: VideoServicePlayback
  ) {
    super();

    this.setupBus();
    this.setupPlayback();
  }

  configure(config: VideoServiceConfig): void {
    // Only set the config if it hasn't been set before
    if (this.config) return;
    this.config = config;
    this.videoPlayerConfig = config.videoPlayerConfig;
  }

  updateVideoPlayerConfig(config: VideoPlayerConfig): void {
    if (!this.videoPlayerConfig) return;
    this.videoPlayerConfig = config;
  }

  setVideoContainer(container: HTMLDivElement): void {
    this.bus.setVideoContainer(container);
  }

  getPlaybackStats(): void {
    this.bus.dispatchVideoPlayerEvent(HostEvent.GetPlaybackStats);
  }

  private handlePlaybackChange(enabled: boolean): void {
    this.bus.dispatchVideoPlayerEvent(HostEvent.SetVolume, this.playback.volume);
    if (!enabled) {
      this.getPlaybackStats();
    }
  }

  private attemptSetup(): void {
    // Start only if the video player config is set and the bus is ready
    if (this.videoPlayerConfig && this.bus.ready) {
      this.bus.dispatchVideoPlayerEvent(HostEvent.Setup, { config: this.videoPlayerConfig });
    }
  }

  private attemptStart(): void {
    // Start only if the video player is ready and the playback is enabled
    if (this.ready && this.playback.enabled) {
      this.startPlayback();
    } else {
      this.bus.dispatchVideoPlayerEvent(HostEvent.Stop);
    }
  }

  private startPlayback(): void {
    if (!this.config) {
      console.error('Starting playback without config');
      return;
    }
    this.bus.dispatchVideoPlayerEvent(HostEvent.Start, this.config);
    this.bus.dispatchVideoPlayerEvent(HostEvent.SetVolume, this.playback.volume);
  }

  private setupBus(): void {
    this.bus.addEventListener('ready', () => {
      this.attemptSetup();
    });

    this.bus.onVideoPlayerEvent(VideoPlayerEvent.PlayerLoaded, (event) => {
      const { version, name, videoElement } = event;
      this.version = version;
      this.name = name;
      this.videoElement = videoElement;
      if (this.videoElement) {
        this.dispatchEvent('videoElement', this.videoElement);
      }
      this.ready = true;
    });
    this.bus.onVideoPlayerEvent(VideoPlayerEvent.PlaybackStats, (stats) => {
      this.dispatchEvent('playbackStats', stats);
    });
    this.bus.onVideoPlayerEvent(VideoPlayerEvent.LatencyChange, (latency) => {
      this.dispatchEvent('videoLatency', latency);
    });
    this.bus.onVideoPlayerEvent(VideoPlayerEvent.ForcedAutoChannelSwitch, (channel) => {
      this.dispatchEvent('qualityLimit', channel ?? StreamQuality.High);
    });
    this.bus.onVideoPlayerEvent(VideoPlayerEvent.ResetStreamQualityLimit, (quality) => {
      this.dispatchEvent('resetStreamQualityLimit', quality ?? StreamQuality.High);
    });
    this.bus.onVideoPlayerEvent(VideoPlayerEvent.Playing, ({ playing }) => {
      this.playback.setPlaying(playing);
    });
  }

  private setupPlayback(): void {
    this.playback.addEventListener('enabled', (enabled) => {
      this.handlePlaybackChange(enabled);
      this.attemptStart();
    });
    this.playback.addEventListener('channel', (channel) => {
      this.bus.dispatchVideoPlayerEvent(HostEvent.SetChannel, channel);
    });
    this.playback.addEventListener('volume', (volume) => {
      this.bus.dispatchVideoPlayerEvent(HostEvent.SetVolume, volume);
    });
  }

  reset(): void {
    super.clear();
    this.bus.reset();
    this.playback.reset();

    this._ready = false;
    this._videoPlayerConfig = null;
    this.config = null;
    this.videoElement = null;
    this.version = 0;
    this.name = 'unknown';
    this.coldStart = false;

    this.setupBus();
    this.setupPlayback();
  }
}
